import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  TableCell,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import api, { uploadFile } from '../config/api';
import DataTable from '../components/DataTable';

function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    image_url: '',
    target_type: 'all',
    target_ids: '',
    category: '',
    destination: 'app',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');

  useEffect(() => {
    fetchNotifications();
    fetchTrainers();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications');
      setNotifications(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      const response = await api.get('/trainers');
      setTrainers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching trainers:', error);
    }
  };

  const handleOpen = () => {
    setFormData({
      title: '',
      message: '',
      image_url: '',
      target_type: 'all',
      target_ids: '',
      category: '',
      destination: 'app',
    });
    setSelectedFile(null);
    setImagePreview(null);
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedFile(null);
    setImagePreview(null);
    setError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};
    const titleLength = formData.title.trim().length;
    const messageLength = formData.message.trim().length;

    if (!titleLength) {
      newErrors.title = 'Title is required';
    } else if (titleLength < 0 || titleLength > 65) {
      newErrors.title = 'Title should be between 0–65 characters.';
    }
    if (!messageLength) {
      newErrors.message = 'Message is required';
    } else if (messageLength < 0 || messageLength > 150) {
      newErrors.message = 'Message should be between 0–150 characters.';
    }

    if (formData.target_type === 'category' && !formData.category) {
      newErrors.category = 'Category is required when targeting by category';
    }

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    try {
      setError('');
      setUploading(true);

      const submitFormData = new FormData();
      submitFormData.append('title', formData.title);
      submitFormData.append('message', formData.message);
      submitFormData.append('target_type', formData.target_type);
      submitFormData.append('target_ids', formData.target_ids || '');
      submitFormData.append('destination', formData.destination);

      // Add category if target_type is 'category'
      if (formData.target_type === 'category') {
        submitFormData.append('category', formData.category);
      }

      // If new file is selected, append it
      if (selectedFile) {
        submitFormData.append('image', selectedFile);
      } else if (formData.image_url) {
        // If URL is provided, append URL
        submitFormData.append('image_url', formData.image_url);
      }

      const res = await uploadFile('/notifications', submitFormData, 'POST');

      handleClose();
      await fetchNotifications();
      if (res?.data?.data) {
        const { sent, failed } = res.data.data;
        if (sent > 0) {
          setSuccess(`Notification sent successfully to ${sent} device(s)${failed > 0 ? ` (${failed} failed)` : ''}.`);
        } else {
          setError(`Notification recorded, but 0 devices were reachable (${failed} failed or unregistered).`);
        }
      } else {
        setSuccess('Notification request submitted.');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Error sending notification');
    } finally {
      setUploading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };
  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <Box>
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && !open && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Push Notifications</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
          Send Notification
        </Button>
      </Box>

      <DataTable
        data={notifications}
        loading={loading}
        searchFields={['title', 'message']}
        emptyMessage="No notifications found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'title', label: 'Title' },
          { field: 'message', label: 'Message' },
          { field: 'image', label: 'Image' },
          { field: 'target', label: 'Target' },
          { field: 'status', label: 'Status' },
          { field: 'sent_at', label: 'Sent At' },
        ]}
        renderRow={(notification) => (
          <TableRow key={notification.id}>
            <TableCell>{notification.id}</TableCell>
            <TableCell>{notification.title}</TableCell>
            <TableCell>{notification.message}</TableCell>
            <TableCell>
              {notification.image_url ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    setImagePreviewUrl(`${process.env.REACT_APP_API_URL + notification.image_url}`);
                    setImagePreviewOpen(true);
                  }}
                  sx={{
                    p: 0,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    borderRadius: 1,
                    display: 'inline-block',
                  }}
                  aria-label="Preview notification image"
                >
                  <img
                    src={`${process.env.REACT_APP_API_URL + notification.image_url}`}
                    alt=""
                    style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, display: 'block' }}
                  />
                </Box>
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>
              <Chip
                label={notification.target_type === 'paid_users' ? 'paid users' : notification.target_type}
                size="small"
                color={
                  notification.target_type === 'all'
                    ? 'primary'
                    : notification.target_type === 'category'
                      ? 'success'
                      : notification.target_type === 'paid_users'
                        ? 'warning'
                        : 'secondary'
                }
              />
            </TableCell>
            <TableCell>
              <Chip
                label={notification.status}
                size="small"
                color={
                  notification.status === 'sent'
                    ? 'success'
                    : notification.status === 'failed'
                      ? 'error'
                      : 'default'
                }
              />
            </TableCell>
            <TableCell>
              {notification.sent_at
                ? new Date(notification.sent_at).toLocaleString()
                : '-'}
            </TableCell>
          </TableRow>
        )}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Send Push Notification</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            required
            error={Boolean(formErrors.title)}
            helperText={
              formErrors.title ||
              `This will appear as the notification title on the user device. Recommended: 0–65 characters (current: ${formData.title.trim().length}).`
            }
            inputProps={{ maxLength: 80 }}
          />
          <TextField
            fullWidth
            label="Message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            margin="normal"
            required
            multiline
            rows={4}
            error={Boolean(formErrors.message)}
            helperText={
              formErrors.message ||
              `This text is the main body of the push notification. Recommended: 0–150 characters (current: ${formData.message.trim().length}).`
            }
            inputProps={{ maxLength: 200 }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Open app at</InputLabel>
            <Select
              value={formData.destination}
              onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
              label="Open app at"
            >
              <MenuItem value="app">Default app screen</MenuItem>
              <MenuItem value="home_services">Home Services</MenuItem>
              <MenuItem value="online_services">Online Services</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Selected page opens when a user taps the notification.
            </Typography>
          </FormControl>
          <Box sx={{ mb: 2, mt: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="notification-image-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="notification-image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ mb: 1 }}
              >
                {selectedFile ? selectedFile.name : 'Upload Image (Optional)'}
              </Button>
            </label>
            <Typography variant="caption" color="text.secondary">
              Max size: 1MB
            </Typography>
            {/* {!selectedFile && (
              <TextField
                fullWidth
                label="Or Enter Image URL (Optional)"
                value={formData.image_url}
                onChange={(e) => {
                  setFormData({ ...formData, image_url: e.target.value });
                  setImagePreview(e.target.value);
                }}
                margin="normal"
                helperText="Upload image file or provide image URL"
              />
            )} */}
            {imagePreview && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 4 }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {selectedFile && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedFile(null);
                      setImagePreview(null);
                      setFormData({ ...formData, image_url: '' });
                    }}
                    sx={{ mt: 1 }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            )}
          </Box>
          <FormControl fullWidth margin="normal">
            <InputLabel>Target Type</InputLabel>
            <Select
              value={formData.target_type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  target_type: e.target.value,
                  category: '',
                  target_ids: '',
                })
              }
              label="Target Type"
            >
              <MenuItem value="all">All Trainers</MenuItem>
              <MenuItem value="trainers">Specific Trainers (by ID)</MenuItem>
              <MenuItem value="category">By Category</MenuItem>
              <MenuItem value="paid_users">Paid users (plan purchasers)</MenuItem>
            </Select>
          </FormControl>
          {formData.target_type === 'trainers' && (
            <TextField
              fullWidth
              label="Trainer IDs (comma-separated)"
              value={formData.target_ids}
              onChange={(e) => setFormData({ ...formData, target_ids: e.target.value })}
              margin="normal"
              placeholder="e.g., 1,2,3"
              helperText="Leave empty to send to all trainers"
            />
          )}
          {formData.target_type === 'category' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Category"
                required
                error={Boolean(formErrors.category)}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={uploading} >
            {uploading ? <CircularProgress size={20} /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Image preview</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'grey.100',
            minHeight: 200,
          }}
        >
          {imagePreviewUrl ? (
            <Box
              component="img"
              src={imagePreviewUrl}
              alt="Notification attachment"
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagePreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Notifications;